import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, Subscription, tap, throwError, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';

import { Profile } from '@app/models/account/profile';
import { SignUpRequest } from '@app/models/account/sign-up-request';
import { User } from '@app/services/authentication/user';
import { User as SentryUser, setUser } from '@sentry/angular';
import { SDate } from '@summit/core';
import { SummitAuthService } from '@summit/services';
import { ApiError } from '@summit/ui';

import { LayoutService } from '@summit/ui';
import { MessageService } from 'primeng/api';
import { colorConsole, debugBuild } from '../../../app-debug';

const debug = false;
// Centralized storage keys
const AUTH_STORAGE_KEYS = {
  user: 'currentUser',
  profile: 'currentEmployee'
} as const;

function safeParse<T>(raw: string | null, debugLabel: string): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    if (debug) console.warn(`[AUTH] Failed parsing ${debugLabel}`, err);
    return null;
  }
}
@Injectable({ providedIn: 'root' })
export class AuthenticationService extends SummitAuthService implements OnDestroy {
  private currentUserBS: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;

  private activeProfileBS: BehaviorSubject<Profile | null>;
  public activeProfile$: Observable<Profile | null>;

  private refreshTimer: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private layoutService: LayoutService //private notifications: SummitNotificationService
  ) {
    super();
    const rawUser = localStorage?.getItem(AUTH_STORAGE_KEYS.user);
    const storedUserObj = safeParse<Partial<User>>(rawUser, AUTH_STORAGE_KEYS.user);
    const storedUser = storedUserObj ? new User(storedUserObj) : null;

    const rawProfile = localStorage?.getItem(AUTH_STORAGE_KEYS.profile);
    const storedProfileObj = safeParse<Partial<Profile>>(rawProfile, AUTH_STORAGE_KEYS.profile);
    const storedEmployee = storedProfileObj ? new Profile(storedProfileObj) : null;

    this.currentUserBS = new BehaviorSubject<User | null>(storedUser);
    this.currentUser$ = this.currentUserBS
      .asObservable()
      .pipe(distinctUntilChanged((a, b) => a?.userName === b?.userName));
    this.activeProfileBS = new BehaviorSubject<Profile | null>(storedEmployee);
    this.activeProfile$ = this.activeProfileBS.asObservable().pipe(distinctUntilChanged((a, b) => a?.id === b?.id));

    // console.log(`Loading User until ${this.currentUserSubject.value.expiration}`);
    if (this.currentUserBS.value) {
      if (!this.currentUserBS.value.expiration || this.currentUserBS.value.expiration.isSameOrBefore(SDate.new())) {
        /*
                if(this.currentUserSubject.value.expiration){
                    console.log(`Token Expired: Comparing ${Date.now()} to expiration ${
                        this.currentUserSubject.value.expiration.getTime()}`);
                }
                */
        this.logout();
      } else {
        // refresh token as we load
        //   timer(2000)
        //     .pipe(take(1),tap(() => {console.log(`Refreshing Token`)}))
        //     .subscribe(() => this.refreshToken()); // No Unsubscribe
      }
    }
    //Keep Sentry in sync
    this.activeProfile$.subscribe((profile) => {
      // No Unsubscribe
      if (profile) {
        setUser({
          id: profile.id,
          email: profile.email,
          username: `${profile?.tenant?.abbreviation}: ${profile.name}`,
          segment: profile?.tenant?.name
        } as SentryUser);

        /*
          [key: string]: any;
          id?: string;
          ip_address?: string;
          email?: string;
          username?: string;
          segment?: string;

        https://docs.sentry.io/platforms/javascript/guides/angular/usage/sdk-fingerprinting/
        https://docs.sentry.io/platforms/javascript/guides/angular/enriching-events/breadcrumbs/
        https://docs.sentry.io/platforms/javascript/guides/angular/enriching-events/identify-user/
        */
      } else {
        setUser(null);
      }
    });
  }

  private startTokenRefreshTimer() {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    const timeRemaining = this.currentUserBS.value?.expiration.diff(SDate.new()) ?? 0;
    let timeTillRefresh = timeRemaining * 0.5;
    // timeTillRefresh = timeTillRefresh <= (30 * 1000) ? 0 : timeTillRefresh;
    timeTillRefresh = timeTillRefresh <= 24 * 60 * 60 * 1000 ? 0 : timeTillRefresh;
    // console.log(`Comparing Now ${SDate.new()} to expiration ${
    //    this.currentUserSubject.value.expiration} with delta of ${timeRemaining}`);
    // console.log(`Setting Timer for ${timeTillRefresh}`);
    this.refreshTimer = timer(timeTillRefresh).subscribe(() => {
      console.log(`Timer for refresh triggered`);
      this.refreshToken();
    }); // No Unsubscribe
  }

  public get currentUser(): User | null {
    return this.currentUserBS.value;
  }

  public get activeProfile(): Profile | null {
    return this.activeProfileBS.value;
  }

  processUserResponse(newUser: User) {
    if (newUser) {
      if (newUser.token && newUser.token.length > 0) {
        // ...existing code...
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(newUser));
        const lclActiveProfile = newUser.getProfile();
        if (debug) console.log(`[AUTH] Got Profile from user`, lclActiveProfile, newUser);
        this.currentUserBS.next(newUser);
        localStorage.setItem(AUTH_STORAGE_KEYS.profile, JSON.stringify(lclActiveProfile));
        // ...existing code...
        if (debug) console.log(`[AUTH] Pushing new Profile`, lclActiveProfile, newUser);
        this.activeProfileBS.next(lclActiveProfile);
        if (debugBuild) {
          console.info(`---->`, newUser, ` <---- `);
          console.info(`---->`, lclActiveProfile, ` <---- `);
        }
        this.startTokenRefreshTimer();

        // Show menu instantly after login
        if (this.layoutService.isDesktop()) {
          this.layoutService.state.staticMenuDesktopInactive = false;
        } else {
          // On mobile, menu should be closed after login
          this.layoutService.state.staticMenuMobileActive = false;
        }
        this.layoutService.state = this.layoutService.state;
      }
    }
  }

  override isLoggedIn(): boolean {
    let response: boolean;
    if (this.currentUser && this.currentUser.activeEmployeeId?.length > 0) {
      response = true;
    } else {
      response = false;
    }
    // console.log(`User is Logged In: ${response}`, this.activeEmployeeID);
    return response;
  }
  login(UserName: string, Password: string): Observable<User> {
    const apiPath = `api/Accounts/GenerateToken`;
    //console.log(`Calling ${apiPath}`);
    return this.http.post<Partial<User>>(apiPath, { UserName, Password }).pipe(
      catchError((res: HttpErrorResponse) => {
        //console.error('Authentication Services: Error: ', res.error);
        const apiError: ApiError = res.error;
        return throwError(() => apiError);
      }),
      map((data) => {
        //console.log('Authentication Service: Got Response :', data);
        // login successful if there's a jwt token in the response
        const user = new User(data);
        //console.log('Authentication Service: Got User :', user);
        this.processUserResponse(user);
        return user;
      })
    );
  }

  signUp(request: Partial<SignUpRequest>) {
    return this.http.post<Partial<User>>(`api/Accounts/SignUp`, request).pipe(
      catchError((res: HttpErrorResponse) => {
        //console.error('Authentication Services: Error: ', res.error);
        const apiError: ApiError = res.error;
        return throwError(() => apiError);
      }),
      map((user) => {
        //console.log('Authentication Service: Got Response :', user);
        // login successful if there's a jwt token in the response
        this.processUserResponse(new User(user));
        return user;
      })
    );
  }

  validateTokenIfPresent() {
    if (this.currentUserBS.getValue() || this.activeProfileBS.getValue()) {
      if (debugBuild) colorConsole(`----> Refreshing Token per ValidateToken <----`);
      this.refreshToken();
    }
  }

  refreshToken() {
    this.changeProfile(this.activeProfileBS.value?.id ?? null);
  }

  changeProfile(id: string | null): void {
    if (id == null) {
      return;
    }
    if (debugBuild) console.info(`----> Getting ChangeToken: ${id} <----`);
    this.http
      .post<User>(`api/Accounts/ChangeToken`, { id })
      .pipe(
        tap((user: Partial<User>) => {
          if (debugBuild) colorConsole(`----> Changing User to ${user.userName} <----`);
        }),
        catchError((res: HttpErrorResponse) => {
          //console.log(`Authentication Services: Error: ${res.status}`, res.error);
          const apiError: ApiError = res.error;
          this.messageService.add({
            severity: 'error',
            summary: 'Changing Profile Error',
            detail: `${apiError?.message}`
          });
          return throwError(() => apiError);
        }),
        map((data: Partial<User>) => {
          //console.info(`----> Process user data`, data);
          const newUser = new User(data);
          //console.info(`----> Process to user`, data.divisionConfig);
          this.processUserResponse(newUser);
          this.updateToken(newUser);
        })
      )
      .subscribe(); // No Unsubscribe
  }

  private updateToken(user: User) {
    // console.log('Authentication Service: Got Response :', user);
    // login successful if there's a jwt token in the response
    if (user && user.token && user.token.length > 0) {
      /*
            console.log(`Authentication Service: Saving token ${user.token.substr(0, 10)}...${
                user.token.substr(user.token.length - 10, 10)}`);
                */
      user.expiration = SDate.new(user.expiration);
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));
      this.currentUserBS.next(user);
      this.updateEmployee(user.activeEmployeeId);
      this.startTokenRefreshTimer();
    }
  }

  private updateEmployee(id: string) {
    // console.log(`[Updating Employee] Looking for ${id} in `, this.currentUser.employees);
    const newProfile = this.currentUserBS.value?.employees.find((entry) => entry?.id === id);
    if (newProfile) {
      // console.log(`[Updating Employee] updated to ${id} in `, newProfile);
      if (newProfile.id !== this.activeProfileBS.value?.id) {
        localStorage.setItem(AUTH_STORAGE_KEYS.profile, JSON.stringify(newProfile));
        if (debug) console.log(`[AUTH] Changing user to ${newProfile.name}`, newProfile);
        this.activeProfileBS.next(newProfile);
      }
    } else {
      console.error('Could not find Employee ID in Auth Employee List');
    }
  }

  override logout() {
    // remove user from local storage to log user out
    // console.log('Logged out');
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    if (localStorage) localStorage.removeItem(AUTH_STORAGE_KEYS.user);
    this.currentUserBS.next(null);
    if (localStorage) localStorage.removeItem(AUTH_STORAGE_KEYS.profile);
    if (debug) console.log(`[AUTH] Changing user to null`);
    this.activeProfileBS.next(null);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
  }
}
